import { api } from '@/api/client';
import type { components } from '@/api/generated/schema';
import type {
  SmartLink,
  ServableDomain,
  CreateSmartLinkPayload,
  UpdateSmartLinkPayload,
} from '@/types';

export interface SmartLinksListResponse {
  smartLinks: SmartLink[];
  total: number;
}

/**
 * WEB-745: server-side pagination + search for GET /projects/:id/smart-links.
 * limit/offset mirror backend PaginationQueryDto (limit 1..100, default 20;
 * offset default 0). `status` is the pre-existing filter, now forwarded as a
 * real query param instead of client-side-only.
 */
export interface SmartLinkListParams {
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
  /**
   * WEB-3991: фильтр «Владелец» — id создателей, 1–20; уходит как
   * `ownerIds[]=…` (сборщик адреса axios по умолчанию). Участнику сервер
   * всё равно отдаёт только его тесты.
   */
  ownerIds?: string[];
  /** WEB-3991: фильтр «Дата создания» — ISO-момент, включительно. */
  createdFrom?: string;
}

/** WEB-3991: строка списка владельцев — GET .../smart-links/owners. */
export interface SmartLinkOwnerItem {
  id: string;
  /** Имя как в колонке «Владелец»; пользователя нет — null. */
  name: string | null;
}

/** WEB-745: lightweight option row for filter dropdowns — GET .../smart-links/options. No slug (smart links don't have one). */
export interface SmartLinkOptionItem {
  id: string;
  name: string;
}

/**
 * WEB-3742: тело «Отдать весь трафик победителю» — из контракта сервера.
 * `summary` — «Итог для команды»: null стирает, нет поля — не трогается.
 */
export type FinishSmartLinkPayload =
  components['schemas']['FinishSmartLinkDto'];

/**
 * WEB-3742: тело «Запустить заново» — свободный домен проекта для нового
 * черновика (из контракта сервера).
 */
export type RerunSmartLinkPayload =
  components['schemas']['RerunSmartLinkDto'];

export const smartLinksApi = {
  list: (projectId: string, params?: SmartLinkListParams) =>
    api.get<SmartLinksListResponse>(`/projects/${projectId}/smart-links`, {
      params,
    }),
  // WEB-745: unpaginated {id,name} list for filter dropdowns (BE now caps
  // GET / above at limit=20 by default — dropdowns need every smart link,
  // not just the first page).
  listOptions: (projectId: string) =>
    api.get<SmartLinkOptionItem[]>(`/projects/${projectId}/smart-links/options`),

  /**
   * WEB-3991: варианты фильтра «Владелец» — различные владельцы тестов
   * проекта (по имени, без имени — в конце); участнику — только он сам.
   */
  owners: (projectId: string) =>
    api.get<SmartLinkOwnerItem[]>(`/projects/${projectId}/smart-links/owners`),

  /**
   * List ACTIVE domains for the project domain-picker.
   * GET /projects/:projectId/smart-links/servable-domains (WEB-272).
   */
  servableDomains: (projectId: string) =>
    api.get<ServableDomain[]>(
      `/projects/${projectId}/smart-links/servable-domains`,
    ),

  create: (projectId: string, data: CreateSmartLinkPayload) =>
    api.post<SmartLink>(`/projects/${projectId}/smart-links`, data),

  get: (projectId: string, id: string) =>
    api.get<SmartLink>(`/projects/${projectId}/smart-links/${id}`),

  update: (projectId: string, id: string, data: UpdateSmartLinkPayload) =>
    api.patch<SmartLink>(`/projects/${projectId}/smart-links/${id}`, data),

  delete: (projectId: string, id: string) =>
    api.delete(`/projects/${projectId}/smart-links/${id}`),

  /** Publish a draft smart link (draft → active). */
  activate: (projectId: string, id: string) =>
    api.post<SmartLink>(`/projects/${projectId}/smart-links/${id}/activate`),

  deactivate: (projectId: string, id: string) =>
    api.post<SmartLink>(`/projects/${projectId}/smart-links/${id}/deactivate`),

  /**
   * WEB-3742: «Отдать весь трафик победителю» — веса 100 / 0 одним запросом
   * и отметка «завершён». POST /projects/:projectId/smart-links/:id/finish.
   */
  finish: (projectId: string, id: string, body: FinishSmartLinkPayload) =>
    api.post<SmartLink>(
      `/projects/${projectId}/smart-links/${id}/finish`,
      body,
    ),

  /**
   * WEB-3742: «Запустить заново» — новый черновик с теми же направлениями
   * на другой свободный домен. POST /projects/:projectId/smart-links/:id/rerun.
   */
  rerun: (projectId: string, id: string, body: RerunSmartLinkPayload) =>
    api.post<SmartLink>(
      `/projects/${projectId}/smart-links/${id}/rerun`,
      body,
    ),

  /**
   * WEB-337 MOVE: перенести A/B-тест в другой проект той же орги.
   * POST /projects/:projectId/smart-links/:id/move { targetProjectId }
   * BE: очищает directions, статус → DRAFT, домен остаётся. Cross-org→403, same→400.
   */
  move: (projectId: string, id: string, targetProjectId: string) =>
    api.post<SmartLink>(`/projects/${projectId}/smart-links/${id}/move`, {
      targetProjectId,
    }),
};
